@if (node && context) {
<div class="flex flex-column gap-3 p-3 border-1 surface-border border-round-lg surface-card"
     [class.ql-builder-node-child]="!isRoot"
     [attr.data-test]="'ql-builder-node-' + node.alias">

    <!-- header -->
    <div class="flex flex-wrap align-items-center justify-content-between gap-2">
        <div class="flex align-items-center gap-2 min-w-0">
            <i class="fa-regular text-primary" [class.fa-cube]="isRoot" [class.fa-link]="!isRoot"></i>
            <span class="font-semibold text-overflow-ellipsis white-space-nowrap overflow-hidden">{{ headerTitle }}</span>
            <p-tag [value]="node.alias" severity="secondary" pTooltip="Alias in der Abfrage"></p-tag>
            @if (entityAlias && !isRoot) {
                <span class="text-sm text-color-secondary">{{ entityAlias }}</span>
            }
            @if (joinNode?.relation === 'oneToN' || joinNode?.relation === 'manyToN') {
                <p-tag value="1:n" severity="warn"
                       pTooltip="Kann Zeilen vervielfachen: je zugehörigem Eintrag entsteht eine eigene Zeile."></p-tag>
            }
        </div>
        @if (!isRoot) {
            <p-button icon="fa-regular fa-trash-can" severity="danger" [text]="true" [rounded]="true"
                      pTooltip="Verknüpfung entfernen" ariaLabel="Verknüpfung entfernen"
                      data-test="ql-builder-remove-join"
                      (onClick)="remove.emit()"></p-button>
        }
    </div>

    <!-- join settings -->
    @if (joinNode; as join) {
        <div class="flex flex-wrap align-items-center gap-3">
            <p-select [options]="joinTypeOptions" optionLabel="label" optionValue="value"
                      [ngModel]="join.joinType" (ngModelChange)="onJoinTypeChange($event)"
                      styleClass="w-12rem" appendTo="body"
                      pTooltip="Optional: Datensätze ohne Treffer bleiben erhalten. Pflicht: nur Datensätze mit Treffer."></p-select>
            @if (join.relation === 'oneToN') {
                <div class="flex align-items-center gap-2">
                    <p-checkbox [binary]="true" inputId="onlyFirst-{{ join.alias }}"
                                [ngModel]="!!join.onlyFirst" (ngModelChange)="onOnlyFirstChange($event)"></p-checkbox>
                    <label for="onlyFirst-{{ join.alias }}" class="text-sm">nur erster Eintrag</label>
                </div>
            }
        </div>
        @if (join.relation === 'oneToN' && join.onlyFirst) {
            <p-message severity="info" styleClass="w-full" size="small" [text]="textOnlyFirstNoSort"
                       data-test="ql-builder-only-first-no-sort"></p-message>
        }
        @if ((join.relation === 'oneToN' || join.relation === 'manyToN') && !join.onlyFirst) {
            <p-message severity="warn" styleClass="w-full" size="small"
                       text="Diese Verknüpfung kann Zeilen vervielfachen. „nur erster Eintrag“ liefert eine Zeile je Datensatz."></p-message>
        }
    }

    <!-- metadata states -->
    @switch (metaState) {
        @case ('loading') {
            <div class="flex flex-column gap-2" data-test="ql-builder-node-loading">
                <p-skeleton height="2.5rem"></p-skeleton>
                <p-skeleton height="2.5rem" width="70%"></p-skeleton>
            </div>
        }
        @case ('error') {
            <p-message severity="error" styleClass="w-full"
                       text="Die Metadaten dieser Entität konnten nicht geladen werden. Bitte später erneut versuchen."></p-message>
        }
        @case ('none') {
            <p-message severity="error" styleClass="w-full"
                       text="Die Ziel-Entität dieser Verknüpfung konnte nicht ermittelt werden."></p-message>
        }
    }

    @if (metaState === 'ready') {

        <!-- fields -->
        @if (fieldsReplacedByGroupBy) {
            <span class="text-sm text-color-secondary">
                Im Gruppieren-Modus bestimmen Gruppierungsfelder und Kennzahlen die Spalten (siehe unten).
            </span>
        } @else {
            <div class="flex flex-column gap-2">
                <div class="flex align-items-center justify-content-between">
                    <span class="font-medium">Felder</span>
                    <span class="text-sm text-color-secondary">{{ fieldCount }} / {{ limits.maxFieldsPerNode }} Felder</span>
                </div>
                @if (context.groupByMode) {
                    <span class="text-sm text-color-secondary">Im Gruppieren-Modus werden diese Felder gruppiert.</span>
                }
                @if (regularOptions.length) {
                    <p-multiSelect [options]="regularOptions" optionLabel="label" optionValue="value"
                                   [ngModel]="selected.regular" (ngModelChange)="onSectionChange('regular', $event)"
                                   [filter]="true" filterBy="label,value" display="chip" appendTo="body"
                                   [selectionLimit]="selectionLimit('regular')"
                                   placeholder="Felder wählen" styleClass="w-full"
                                   [maxSelectedLabels]="30" selectedItemsLabel="{0} Felder gewählt"
                                   emptyFilterMessage="Keine Treffer" emptyMessage="Keine Felder vorhanden"
                                   filterPlaceHolder="Suchen"
                                   data-test="ql-builder-fields-regular"></p-multiSelect>
                } @else {
                    <span class="text-sm text-color-secondary">Keine Felder vorhanden.</span>
                }

                @if (genericOptions.length) {
                    <div class="flex align-items-center gap-2 mt-2">
                        <span class="font-medium">Generische Felder</span>
                        <p-tag value="umgebungsgebunden" severity="warn"
                               pTooltip="Generische Felder werden über ihre ID angesprochen – die Definition ist dann nicht zwischen Umgebungen übertragbar."></p-tag>
                    </div>
                    <p-multiSelect [options]="genericOptions" optionLabel="label" optionValue="value"
                                   [ngModel]="selected.generic" (ngModelChange)="onSectionChange('generic', $event)"
                                   [filter]="true" filterBy="label,value,group" display="chip" appendTo="body"
                                   [selectionLimit]="selectionLimit('generic')"
                                   placeholder="Generische Felder wählen" styleClass="w-full"
                                   [maxSelectedLabels]="30" selectedItemsLabel="{0} Felder gewählt"
                                   emptyFilterMessage="Keine Treffer" emptyMessage="Keine Felder vorhanden"
                                   filterPlaceHolder="Suchen"
                                   data-test="ql-builder-fields-generic">
                        <ng-template #item let-option>
                            <div class="flex align-items-center justify-content-between gap-2 w-full">
                                <span>{{ option.label }}</span>
                                <span class="text-sm text-color-secondary">{{ option.group ?? 'alle Typen' }}</span>
                            </div>
                        </ng-template>
                    </p-multiSelect>
                }

                @if (objectGenericsHiddenByGroupBy) {
                    <span class="text-sm text-color-secondary" data-test="ql-builder-generic-object-group-by">
                        <i class="fa-regular fa-circle-info mr-1"></i>{{ textGenericObjectGroupBy }}
                    </span>
                }

                @if (eavOptions.length) {
                    <span class="font-medium mt-2">EAV-Felder</span>
                    <p-multiSelect [options]="eavOptions" optionLabel="label" optionValue="value"
                                   [ngModel]="selected.eav" (ngModelChange)="onSectionChange('eav', $event)"
                                   [filter]="true" display="chip" appendTo="body"
                                   [selectionLimit]="selectionLimit('eav')"
                                   placeholder="EAV-Felder wählen" styleClass="w-full"
                                   [maxSelectedLabels]="30" selectedItemsLabel="{0} Felder gewählt"
                                   emptyFilterMessage="Keine Treffer" emptyMessage="Keine Felder vorhanden"
                                   filterPlaceHolder="Suchen"
                                   data-test="ql-builder-fields-eav"></p-multiSelect>
                }
                @if (fieldCount >= limits.maxFieldsPerNode) {
                    <span class="text-sm text-yellow-600">Maximal {{ limits.maxFieldsPerNode }} Felder je Knoten erreicht.</span>
                }
            </div>
        }

        <!-- filter -->
        <div class="flex flex-wrap align-items-center gap-2">
            <p-button [label]="dialogFilterCount ? 'Filter (' + dialogFilterCount + ')' : 'Filter'"
                      icon="fa-regular fa-filter" severity="secondary" [outlined]="true" size="small"
                      data-test="ql-builder-filter"
                      (onClick)="openFilterDialog()"></p-button>
            @if (dateFieldOptions.length) {
                <p-button label="Relativer Zeitraum" icon="fa-regular fa-calendar-days" severity="secondary"
                          [outlined]="true" size="small" [disabled]="!!relativeDateDraft"
                          [pTooltip]="textRelativeDate"
                          data-test="ql-builder-relative-date-add"
                          (onClick)="openRelativeDate()"></p-button>
            }
            @if (dialogFilterCount) {
                <span class="text-sm text-color-secondary">{{ filterSummary() }}</span>
            }
        </div>

        <!-- relative date filters (chips) -->
        @if (relativeFilterChips.length) {
            <div class="flex flex-wrap align-items-center gap-2" data-test="ql-builder-relative-date-chips">
                @for (chip of relativeFilterChips; track chip.index) {
                    <div class="flex align-items-center gap-1">
                        <p-chip [label]="chip.label" icon="fa-regular fa-calendar-days" [removable]="true"
                                [pTooltip]="chip.value" [attr.data-test]="'ql-builder-relative-date-chip-' + chip.index"
                                (onRemove)="removeRelativeFilter(chip.index)"></p-chip>
                        @if (chip.editable) {
                            <p-button icon="fa-regular fa-pen" [text]="true" [rounded]="true" severity="secondary"
                                      size="small" pTooltip="Relativen Zeitraum ändern" ariaLabel="Relativen Zeitraum ändern"
                                      [disabled]="!!relativeDateDraft"
                                      (onClick)="editRelativeFilter(chip.index)"></p-button>
                        }
                    </div>
                }
            </div>
        }

        <!-- relative date editor -->
        @if (relativeDateDraft; as draft) {
            <mvs-ql-builder-relative-date [fields]="dateFieldOptions"
                                          [alias]="node.alias"
                                          [criterion]="draft.criterion"
                                          (apply)="onRelativeDateApply($event)"
                                          (cancel)="cancelRelativeDate()"></mvs-ql-builder-relative-date>
        }

        <!-- leaf children: extended -->
        @for (child of extendedChildren(); track child.alias) {
            <div class="flex align-items-center justify-content-between gap-2 p-2 border-1 surface-border border-round-lg"
                 [attr.data-test]="'ql-builder-extended-' + child.alias">
                <div class="flex flex-column gap-1 min-w-0">
                    <div class="flex align-items-center gap-2">
                        <i class="fa-regular fa-calculator text-primary"></i>
                        <span class="font-medium">{{ extendedLabel(child) }}</span>
                        <p-tag [value]="child.alias" severity="secondary"></p-tag>
                        <p-tag value="berechnet" severity="info"></p-tag>
                    </div>
                    <span class="text-sm text-color-secondary">
                        Wird pro Zeile berechnet – nicht filterbar, nicht sortierbar.
                    </span>
                    @if (child.resultKind === 'entity') {
                        <p-message severity="error" size="small" styleClass="w-full"
                                   text="Erweiterte Verknüpfungen (Objekte) werden nicht unterstützt – bitte entfernen."
                                   data-test="ql-builder-extended-join-unsupported"></p-message>
                    }
                </div>
                <p-button icon="fa-regular fa-trash-can" severity="danger" [text]="true" [rounded]="true"
                          pTooltip="Entfernen" ariaLabel="Erweitertes Feld entfernen"
                          (onClick)="removeChild(child.alias)"></p-button>
            </div>
        }

        <!-- leaf children: side-car -->
        @for (child of sideCarChildren(); track child.alias) {
            <div class="flex flex-column gap-2 p-2 border-1 surface-border border-round-lg"
                 [attr.data-test]="'ql-builder-side-car-' + child.alias">
                <div class="flex align-items-center justify-content-between gap-2">
                    <div class="flex align-items-center gap-2 min-w-0">
                        <i class="fa-regular fa-puzzle-piece text-primary"></i>
                        <span class="font-medium">{{ sideCarRow(child)?.label ?? child.instanceAlias }}</span>
                        <p-tag [value]="child.alias" severity="secondary"></p-tag>
                        <p-tag value="umgebungsgebunden" severity="warn"
                               pTooltip="Version und Felder werden über IDs angesprochen – nicht zwischen Umgebungen übertragbar."></p-tag>
                    </div>
                    <div class="flex align-items-center">
                        <p-button icon="fa-regular fa-pen" [text]="true" [rounded]="true" severity="secondary"
                                  pTooltip="Version und Felder ändern" ariaLabel="Zusatzfelder bearbeiten"
                                  (onClick)="editSideCar(child)"></p-button>
                        <p-button icon="fa-regular fa-trash-can" severity="danger" [text]="true" [rounded]="true"
                                  pTooltip="Entfernen" ariaLabel="Zusatzfelder entfernen"
                                  (onClick)="removeChild(child.alias)"></p-button>
                    </div>
                </div>
                @if (sideCarInfo[child.alias]; as info) {
                    @if (info.loading) {
                        <p-skeleton height="1.25rem" width="60%"></p-skeleton>
                    } @else {
                        <span class="text-sm text-color-secondary" [attr.data-test]="'ql-builder-side-car-info-' + child.alias">
                            {{ info.scopeLabel ?? ('Version #' + child.scopeId) }} · {{ info.fieldLabels.join(', ') }} · nicht filterbar
                        </span>
                    }
                } @else {
                    <span class="text-sm text-color-secondary">
                        Version #{{ child.scopeId }} · {{ child.typeFieldIds.length }} Feld(er) · nicht filterbar
                    </span>
                }
                @if (showNotUniqueHint(sideCarRow(child))) {
                    <p-message severity="warn" size="small" styleClass="w-full" [text]="textNotUnique"
                               data-test="ql-builder-side-car-not-unique"></p-message>
                }
            </div>
        }

        <!-- regular join children (recursive) -->
        @for (child of joinChildren(); track child.alias) {
            <mvs-ql-builder-node [node]="child"
                                 [entityAlias]="childInfo[child.alias]?.entityAlias ?? null"
                                 [title]="childInfo[child.alias]?.title ?? child.name"
                                 [context]="childContext"
                                 [isRoot]="false"
                                 (nodeChange)="onChildChange(child.alias, $event)"
                                 (remove)="removeChild(child.alias)"></mvs-ql-builder-node>
        }

        <!-- add join -->
        <div class="flex align-items-center gap-2">
            <span [pTooltip]="addChildBlockedReason ?? ''">
                <p-button label="Verknüpfung hinzufügen" icon="fa-solid fa-plus" severity="secondary" [outlined]="true" size="small"
                          [disabled]="!!addChildBlockedReason"
                          data-test="ql-builder-add-join"
                          (onClick)="openJoinDialog()"></p-button>
            </span>
            @if (addChildBlockedReason) {
                <span class="text-sm text-color-secondary">{{ addChildBlockedReason }}</span>
            }
        </div>
    }
</div>

<!-- "Verknüpfung hinzufügen": wide modal dialog — search, one tab per category, ONE scroll area; the side-car flow inside -->
@if (joinDialogVisible) {
    <p-dialog [header]="joinDialogHeader"
              [visible]="joinDialogVisible"
              (visibleChange)="onJoinDialogVisibleChange($event)"
              [modal]="true"
              [draggable]="false"
              [resizable]="false"
              appendTo="body"
              styleClass="ql-builder-join-dialog"
              contentStyleClass="flex flex-column"
              data-test="ql-builder-join-dialog">

        <!-- own Escape handler: p-dialog ignores Escape while another overlay (e.g. a toast) holds the top z-index -->
        <div class="flex flex-column gap-3 flex-1 min-h-0" data-test="ql-builder-join-menu"
             (keydown.escape)="closeJoinDialog()">

            @if (sideCarDraft; as draft) {
                <!-- side-car flow: version (left) → fields (right) -->
                <div class="flex flex-column gap-3" data-test="ql-builder-side-car-draft">
                    <div class="flex flex-wrap align-items-center gap-2">
                        <i class="fa-regular fa-puzzle-piece text-primary"></i>
                        <span class="font-semibold">Zusatzfelder: {{ draft.sideCar.label || draft.sideCar.instanceAlias }}</span>
                        @if (draft.sideCar.instanceAlias !== draft.sideCar.label) {
                            <span class="text-sm text-color-secondary">{{ draft.sideCar.instanceAlias }}</span>
                        }
                        <p-tag value="umgebungsgebunden" severity="warn"
                               pTooltip="Version und Felder werden über IDs angesprochen – nicht zwischen Umgebungen übertragbar."></p-tag>
                    </div>
                    @if (showNotUniqueHint(draft.sideCar)) {
                        <p-message severity="warn" size="small" styleClass="w-full" [text]="textNotUnique"
                                   data-test="ql-builder-side-car-draft-not-unique"></p-message>
                    }

                    <div class="grid">
                        <div class="col-12 md:col-5 flex flex-column gap-2">
                            <span class="font-medium">1. Version wählen</span>
                            @switch (draft.scopesState) {
                                @case ('loading') {
                                    <p-skeleton height="2.5rem"></p-skeleton>
                                    <p-skeleton height="2.5rem" width="70%"></p-skeleton>
                                }
                                @case ('error') {
                                    <p-message severity="error" size="small" styleClass="w-full"
                                               text="Die Versionen konnten nicht geladen werden."></p-message>
                                }
                                @default {
                                    @if (draft.scopes?.length) {
                                        <p-listbox [options]="draft.scopes" optionLabel="label" optionValue="id"
                                                   [ngModel]="draft.scopeId" (ngModelChange)="onSideCarScopeChange($event)"
                                                   [filter]="true" filterPlaceHolder="Version suchen" scrollHeight="18rem"
                                                   emptyFilterMessage="Keine Treffer" emptyMessage="Keine Versionen vorhanden"
                                                   searchMessage="{0} Treffer" emptySelectionMessage="Keine Version gewählt"
                                                   selectionMessage="{0} gewählt" ariaLabel="Version wählen"
                                                   styleClass="w-full"
                                                   data-test="ql-builder-side-car-scopes"></p-listbox>
                                    } @else {
                                        <span class="text-sm text-color-secondary">Für diese Zusatzfelder ist noch keine Version mit Feldern angelegt.</span>
                                    }
                                }
                            }
                        </div>
                        <div class="col-12 md:col-7 flex flex-column gap-2">
                            <div class="flex align-items-center justify-content-between">
                                <span class="font-medium">2. Felder wählen</span>
                                @if (draft.fieldsState === 'ready') {
                                    <span class="text-sm" [class.text-red-600]="sideCarFieldsOverLimit"
                                          [class.text-color-secondary]="!sideCarFieldsOverLimit">
                                        {{ draft.selectedFieldIds.length }} / {{ limits.maxSideCarFields }} Felder
                                    </span>
                                }
                            </div>
                            @switch (draft.fieldsState) {
                                @case ('idle') {
                                    <span class="text-sm text-color-secondary">Zuerst links eine Version wählen.</span>
                                }
                                @case ('loading') {
                                    <p-skeleton height="2.5rem"></p-skeleton>
                                    <p-skeleton height="2.5rem" width="70%"></p-skeleton>
                                }
                                @case ('error') {
                                    <p-message severity="error" size="small" styleClass="w-full"
                                               text="Die Felder der Version konnten nicht geladen werden."></p-message>
                                }
                                @case ('ready') {
                                    @if (draft.fieldOptions?.length) {
                                        <p-listbox [options]="draft.fieldOptions" optionLabel="label" optionValue="value"
                                                   optionDisabled="disabled" [multiple]="true" [checkbox]="true"
                                                   [ngModel]="draft.selectedFieldIds" (ngModelChange)="onSideCarFieldsChange($event)"
                                                   [filter]="true" filterPlaceHolder="Feld suchen" scrollHeight="18rem"
                                                   emptyFilterMessage="Keine Treffer" emptyMessage="Keine Felder vorhanden"
                                                   searchMessage="{0} Treffer" emptySelectionMessage="Keine Felder gewählt"
                                                   selectionMessage="{0} Felder gewählt" ariaLabel="Felder wählen"
                                                   styleClass="w-full"
                                                   data-test="ql-builder-side-car-fields">
                                            <ng-template #item let-option>
                                                <div class="flex align-items-center justify-content-between gap-2 w-full"
                                                     [class.text-color-secondary]="option.disabled">
                                                    <span>{{ option.label }}</span>
                                                    @if (option.disabled) {
                                                        <span class="text-xs"><i class="fa-regular fa-ban mr-1"></i>{{ option.reason }}</span>
                                                    }
                                                </div>
                                            </ng-template>
                                        </p-listbox>
                                        @if (sideCarFieldsOverLimit) {
                                            <span class="text-sm text-red-600">Maximal {{ limits.maxSideCarFields }} Felder je Zusatzfeld-Verknüpfung.</span>
                                        }
                                    } @else {
                                        <span class="text-sm text-color-secondary">Diese Version hat keine Felder.</span>
                                    }
                                }
                            }
                        </div>
                    </div>
                </div>
            } @else {
                <!-- search (first focusable element: the dialog focuses it on open) -->
                <p-iconfield>
                    <p-inputicon styleClass="fa-regular fa-magnifying-glass"></p-inputicon>
                    <input pInputText type="text" class="w-full" placeholder="Verknüpfung oder Entität suchen …"
                           [ngModel]="joinSearch" (ngModelChange)="onJoinSearchChange($event)"
                           (keydown.enter)="onJoinSearchEnter()"
                           data-test="ql-builder-join-search"/>
                </p-iconfield>

                <p-tabs [value]="joinCategory" (valueChange)="onJoinCategoryChange($event)" class="flex-1 min-h-0">
                    <p-tablist>
                        @for (section of joinDialogSections; track section.key) {
                            <!-- p-tab stops the propagation of EVERY keydown, so Escape on a focused tab needs its own binding -->
                            <p-tab [value]="section.key" [attr.data-test]="'ql-builder-menu-tab-' + section.key"
                                   (keydown.escape)="closeJoinDialog()">
                                <span class="flex align-items-center gap-2">
                                    {{ section.title }}
                                    <p-tag [value]="sectionCounter(section)" severity="secondary" [rounded]="true"></p-tag>
                                </span>
                            </p-tab>
                        }
                    </p-tablist>
                    <p-tabpanels class="flex-1 min-h-0 overflow-y-auto">
                        @for (section of joinDialogSections; track section.key) {
                            <p-tabpanel [value]="section.key">
                                <div class="flex flex-column gap-2" [attr.data-test]="'ql-builder-menu-' + section.key">
                                    @if (section.blockedReason) {
                                        <p-message severity="warn" size="small" styleClass="w-full" [text]="section.blockedReason"></p-message>
                                    } @else if (section.hint) {
                                        <span class="text-sm" [class.text-yellow-700]="section.hintSeverity === 'warn'"
                                              [class.text-color-secondary]="section.hintSeverity !== 'warn'">
                                            <i class="mr-1 fa-regular" [class.fa-triangle-exclamation]="section.hintSeverity === 'warn'"
                                               [class.fa-circle-info]="section.hintSeverity !== 'warn'"></i>{{ section.hint }}
                                        </span>
                                    }
                                    @if (section.loading) {
                                        <p-skeleton height="2.5rem"></p-skeleton>
                                        <p-skeleton height="2.5rem" width="80%"></p-skeleton>
                                        <p-skeleton height="2.5rem" width="60%"></p-skeleton>
                                    } @else if (!section.items.length) {
                                        <span class="text-sm text-color-secondary p-2">{{ section.emptyText }}</span>
                                    } @else {
                                        <div class="flex flex-column gap-1">
                                            @for (item of section.items; track item.key) {
                                                @if (item.disabledReason) {
                                                    <div class="p-2 border-round-md text-color-secondary ql-builder-join-row-disabled"
                                                         [attr.data-test]="'ql-builder-menu-item-' + item.key" aria-disabled="true">
                                                        <ng-container *ngTemplateOutlet="joinRow; context: {item: item}"></ng-container>
                                                    </div>
                                                } @else {
                                                    <p-button [text]="true" severity="secondary" styleClass="w-full p-2 text-left"
                                                              [attr.data-test]="'ql-builder-menu-item-' + item.key"
                                                              (onClick)="addFromMenu(item)">
                                                        <ng-container *ngTemplateOutlet="joinRow; context: {item: item}"></ng-container>
                                                    </p-button>
                                                }
                                            }
                                        </div>
                                    }
                                </div>
                            </p-tabpanel>
                        }
                    </p-tabpanels>
                </p-tabs>
            }
        </div>

        <ng-template #footer>
            @if (sideCarDraft; as draft) {
                <div class="flex flex-wrap align-items-center justify-content-between gap-2 w-full">
                    <div>
                        @if (!draft.existing) {
                            <p-button label="Zurück zur Auswahl" icon="fa-regular fa-arrow-left" severity="secondary" [text]="true"
                                      data-test="ql-builder-side-car-back"
                                      (onClick)="backToJoinList()"></p-button>
                        }
                    </div>
                    <div class="flex align-items-center gap-2">
                        <p-button label="Abbrechen" severity="secondary" [text]="true"
                                  data-test="ql-builder-side-car-cancel"
                                  (onClick)="cancelSideCarDraft()"></p-button>
                        <p-button label="Übernehmen" icon="fa-regular fa-check" [disabled]="!sideCarDraftApplicable"
                                  data-test="ql-builder-side-car-apply"
                                  (onClick)="applySideCarDraft()"></p-button>
                    </div>
                </div>
            } @else {
                <div class="flex align-items-center justify-content-between gap-2 w-full">
                    <span class="text-sm text-color-secondary">Enter übernimmt den ersten Treffer, Esc schließt.</span>
                    <p-button label="Schließen" severity="secondary" [text]="true"
                              data-test="ql-builder-join-close"
                              (onClick)="closeJoinDialog()"></p-button>
                </div>
            }
        </ng-template>
    </p-dialog>
}

<!-- one row of the dialog list: label (bold), technical name (small, grey; hidden when equal), relation, warning, reason -->
<ng-template #joinRow let-item="item">
    <div class="flex align-items-start gap-3 w-full">
        <div class="flex flex-column min-w-0 flex-1">
            <span class="font-semibold text-color">{{ item.label }}</span>
            @if (item.secondary && item.secondary !== item.label) {
                <span class="text-sm text-color-secondary">{{ item.secondary }}</span>
            }
        </div>
        @if (item.relationHint) {
            <span class="text-sm text-color-secondary w-16rem flex-none">{{ item.relationHint }}</span>
        }
        @if (item.warning) {
            <span class="text-sm text-yellow-700 flex-none"><i class="fa-regular fa-triangle-exclamation mr-1"></i>{{ item.warning }}</span>
        }
        @if (item.disabledReason) {
            <span class="text-sm flex-1 text-right"><i class="fa-regular fa-ban mr-1"></i>{{ item.disabledReason }}</span>
        }
    </div>
</ng-template>

<!-- filter dialog: the shared condition builder, hosted here with an entity-sourced WidgetData (QA #1, see TS) -->
@if (filterDialogVisible && entityAlias && filterDialogWidgetData) {
    <p-dialog [header]="'Filter: ' + headerTitle"
              [visible]="filterDialogVisible"
              (visibleChange)="onFilterDialogVisibleChange($event)"
              [modal]="true"
              [draggable]="false"
              [resizable]="false"
              appendTo="body"
              styleClass="ql-builder-node-filter-dialog"
              data-test="ql-builder-filter-dialog">
        <mvs-condition-builder [objectTypeAlias]="entityAlias"
                               [widgetData]="filterDialogWidgetData"
                               [filterCriteria]="filterDialogCriteria"
                               (onChange)="onFilterChange($event)"></mvs-condition-builder>
        <ng-template #footer>
            <div class="flex flex-wrap align-items-center justify-content-between gap-2 w-full">
                <p-button label="Filter entfernen" icon="fa-regular fa-trash-can" severity="danger" [text]="true"
                          [disabled]="!dialogFilterCount && !filterDraft"
                          data-test="ql-builder-filter-delete"
                          (onClick)="onFilterDelete()"></p-button>
                <div class="flex align-items-center gap-2">
                    <p-button label="Abbrechen" severity="secondary" [text]="true"
                              data-test="ql-builder-filter-cancel"
                              (onClick)="onFilterCancel()"></p-button>
                    <p-button label="Übernehmen" icon="fa-regular fa-check"
                              [disabled]="!filterDialogApplicable"
                              data-test="ql-builder-filter-apply"
                              (onClick)="applyFilterDialog()"></p-button>
                </div>
            </div>
        </ng-template>
    </p-dialog>
}
}
